// @ts-nocheck
import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { ScrollArea } from './ui/scroll-area';
import { Clock, User } from 'lucide-react';
import { supabase } from '@product-radar/shared';
import { useWorkspace, useRegionalSettings } from '@/contexts/context';
import { formatDateTime } from '@/lib/formatting';

interface AuditLogProps {
  recordId: string;
  tableName: string;
}

interface AuditEntry {
  id: string;
  user_id: string | null;
  action: string;
  old_values?: any;
  new_values?: any;
  created_at: string;
  user?: {
    full_name: string;
    email: string;
  };
}

export function AuditLog({ recordId, tableName }: AuditLogProps) {
  const { workspace } = useWorkspace();
  const regionalSettings = useRegionalSettings();
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (workspace && recordId) {
      loadAuditLog();
    }
  }, [workspace, recordId]);

  const loadAuditLog = async () => {
    if (!workspace) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('audit_logs')
        .select(`
          id,
          user_id,
          action,
          old_values,
          new_values,
          created_at,
          user_id,
          user:profiles!audit_logs_user_id_fkey(full_name, email)
        `)
        .eq('record_id', recordId)
        .eq('table_name', tableName)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        // If audit_logs table doesn't exist or permission denied, fail silently
        console.log('Audit log not available:', error.message);
        setEntries([]);
        return;
      }
      setEntries(data || []);
    } catch (error: any) {
      console.error('Error loading audit log:', error);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'insert':
        return <Badge className="bg-green-50 text-green-700 border-green-200">Created</Badge>;
      case 'update':
        return <Badge className="bg-blue-50 text-blue-700 border-blue-200">Updated</Badge>;
      case 'delete':
        return <Badge className="bg-red-50 text-red-700 border-red-200">Deleted</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const formatFieldChanges = (entry: AuditEntry) => {
    if (!entry.old_values && !entry.new_values) return null;

    const changes: string[] = [];
    
    if (entry.new_values && entry.old_values) {
      // Update - show what changed
      Object.keys(entry.new_values).forEach(key => {
        if (key !== 'updated_at' && entry.new_values[key] !== entry.old_values[key]) {
          const oldVal = entry.old_values[key] || '(empty)';
          const newVal = entry.new_values[key] || '(empty)';
          changes.push(`${key}: ${oldVal} → ${newVal}`);
        }
      });
    } else if (entry.new_values) {
      // Create - show new values
      Object.keys(entry.new_values).forEach(key => {
        if (key !== 'id' && key !== 'created_at' && entry.new_values[key]) {
          changes.push(`${key}: ${entry.new_values[key]}`);
        }
      });
    }

    return changes;
  };

  if (loading) {
    return (
      <Card className="p-4">
        <div className="space-y-3">
          <div className="flex items-center space-x-2 mb-3">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <h3 className="font-medium">Activity Log</h3>
          </div>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center space-x-2 mb-3">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-medium">Activity Log</h3>
        </div>
        <p className="text-sm text-muted-foreground text-center py-4">
          No activity recorded yet
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center space-x-2 mb-3">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <h3 className="font-medium">Activity Log</h3>
      </div>
      
      <ScrollArea className="h-[300px] pr-4">
        <div className="space-y-3">
          {entries.map(entry => {
            const changes = formatFieldChanges(entry);
            
            return (
              <div key={entry.id} className="border-l-2 border-muted pl-3 pb-3 space-y-1">
                <div className="flex items-center justify-between">
                  {getActionBadge(entry.action)}
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(entry.created_at, regionalSettings)}
                  </span>
                </div>
                
                <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span>{entry.user?.full_name || entry.user?.email || (entry.user_id ? 'Unknown user' : 'System')}</span>
                </div>

                {changes && changes.length > 0 && (
                  <div className="text-xs text-muted-foreground space-y-0.5 mt-1">
                    {changes.slice(0, 3).map((change, idx) => (
                      <div key={idx} className="truncate">{change}</div>
                    ))}
                    {changes.length > 3 && (
                      <div className="text-xs italic">+{changes.length - 3} more changes</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </Card>
  );
}